/*
 * Pure cart operations. A cart is an array of lines, one per variant:
 *   { variantId, productId, slug, name, variantName, optionName, swatch,
 *     priceAmount, compareAtAmount, stock, weightGrams, image, quantity }
 * The line keeps a snapshot of what the shopper saw so the cart renders
 * without a round trip. Phase 2 checkout re-prices every line on the server;
 * the snapshot is never trusted for the amount charged.
 */

export const MAX_PER_LINE = 20;

const toInt = (n) => (Number.isFinite(Number(n)) ? Math.floor(Number(n)) : 0);

/** Highest quantity allowed for a line (stock, capped by MAX_PER_LINE). */
export function maxQuantity(line) {
  return Math.max(0, Math.min(toInt(line.stock), MAX_PER_LINE));
}

/** Snapshot of a product + variant for the cart. */
export function lineFrom(product, variant) {
  const image = (variant.image || product.image || (product.images && product.images[0])) ?? null;
  return {
    variantId: variant.id,
    productId: product.id,
    slug: product.slug,
    name: product.name,
    variantName: variant.name,
    optionName: product.optionName || null,
    swatch: variant.swatch || null,
    priceAmount: variant.priceAmount,
    compareAtAmount: variant.compareAtAmount || null,
    stock: variant.stock,
    weightGrams: Number.isFinite(variant.weightGrams) ? variant.weightGrams : null,
    image,
    quantity: 0,
  };
}

/**
 * Add `quantity` of a line. Merges with an existing line for the same variant
 * and never exceeds the stock limit.
 * @returns {{ lines: object[], added: number, limited: boolean }}
 */
export function addLine(lines, line, quantity = 1) {
  const want = Math.max(1, toInt(quantity));
  const existing = lines.find((l) => l.variantId === line.variantId);
  // Fresh snapshot wins for price/stock; quantity accumulates.
  const merged = { ...(existing || {}), ...line, quantity: existing ? existing.quantity : 0 };
  const max = maxQuantity(merged);
  const next = Math.min(merged.quantity + want, max);
  const added = Math.max(0, next - merged.quantity);
  const limited = merged.quantity + want > max;
  if (added === 0) return { lines, added: 0, limited };
  const updated = { ...merged, quantity: next };
  return {
    lines: existing
      ? lines.map((l) => (l.variantId === line.variantId ? updated : l))
      : [...lines, updated],
    added,
    limited,
  };
}

/** Set a line's quantity, clamped to 1..max. 0 or less removes the line. */
export function setQuantity(lines, variantId, quantity) {
  const q = toInt(quantity);
  if (q <= 0) return removeLine(lines, variantId);
  return lines.map((l) =>
    l.variantId === variantId ? { ...l, quantity: Math.min(q, Math.max(1, maxQuantity(l))) } : l
  );
}

export function removeLine(lines, variantId) {
  return lines.filter((l) => l.variantId !== variantId);
}

export function itemCount(lines) {
  return lines.reduce((sum, l) => sum + l.quantity, 0);
}

export function subtotal(lines) {
  return lines.reduce((sum, l) => sum + l.priceAmount * l.quantity, 0);
}

/** Savings against compare-at prices, for the summary. */
export function savings(lines) {
  return lines.reduce(
    (sum, l) =>
      sum + (l.compareAtAmount > l.priceAmount ? (l.compareAtAmount - l.priceAmount) * l.quantity : 0),
    0
  );
}

/** Data read back from storage -> valid lines only. */
export function sanitizeLines(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter(
      (l) =>
        l &&
        typeof l.variantId === 'string' &&
        typeof l.slug === 'string' &&
        typeof l.name === 'string' &&
        Number.isFinite(l.priceAmount) &&
        l.priceAmount >= 0 &&
        Number.isFinite(l.quantity) &&
        l.quantity > 0
    )
    .map((l) => ({ ...l, quantity: Math.min(toInt(l.quantity), Math.max(1, maxQuantity(l))) }));
}

/**
 * Bring the snapshots in the cart up to date with what the server just
 * validated (`POST /public/cart/validate` lines): current price, compare-at
 * price and the quantity the shop can supply. With `fixQuantities`, a line
 * asking for more than that is cut down to it. Lines the server does not know
 * (or reports unavailable) are left as they are: removing them is the
 * shopper's decision.
 */
export function reconcileLines(lines, serverLines, { fixQuantities = false } = {}) {
  const byId = new Map((serverLines || []).map((s) => [s.variantId, s]));
  return lines.map((line) => {
    const s = byId.get(line.variantId);
    if (!s || !Number.isFinite(s.unitPriceAmount) || (s.issues || []).includes('UNAVAILABLE')) return line;
    const next = {
      ...line,
      priceAmount: s.unitPriceAmount,
      compareAtAmount: s.compareAtAmount || null,
      stock: Number.isFinite(s.maxQuantity) ? s.maxQuantity : line.stock,
    };
    if (fixQuantities && next.stock > 0 && next.quantity > maxQuantity(next)) next.quantity = maxQuantity(next);
    return next;
  });
}
